import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BookOpen, Plus, Clock, CheckCircle2, XCircle, Sparkles, CreditCard, Upload, ExternalLink, Wallet, AlertTriangle, RefreshCcw, Apple, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import CourseExamDateInput from "@/components/CourseExamDateInput";
import AccessBanner from "@/components/AccessBanner";
import RenewSubscriptionModal from "@/components/RenewSubscriptionModal";
import PageIntroCard from "@/components/PageIntroCard";
import { isNativeApp, isNativeIOS } from "@/lib/platform";
import IAP from "@/lib/iap";
import SubscriptionStatus from "@/components/SubscriptionStatus";

const STATUS_STYLES = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, label: "Pending Approval" },
  approved: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Approved" },
  rejected: { color: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle, label: "Rejected" },
  revoked: { color: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle, label: "Enrollment Revoked" },
  suspended: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Access Suspended" },
};
const PAYMENT_STATUS_STYLES = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending Verification" },
  verified: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Payment Verified" },
  rejected: { color: "bg-rose-100 text-rose-800 border-rose-200", label: "Payment Rejected" },
  not_required: { color: "bg-slate-100 text-slate-700 border-slate-200", label: "Free Enrollment" },
};

const MAX_PROOF_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

export default function StudentEnrollments() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [available, setAvailable] = useState([]);
  const [paySettings, setPaySettings] = useState(null);
  // Custom manual payment systems (GCash, GoTyme, bank transfer QR, etc.)
  // live in a separate collection served by /payment-systems/public — the
  // presence of at least one enabled system is what tells us "payment IS
  // configured" for the amber warning below, so we load it explicitly.
  const [paymentSystems, setPaymentSystems] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [examDatesByCourse, setExamDatesByCourse] = useState({});
  const [dialog, setDialog] = useState({ open: false, course: null, note: "", ref: "", file: null });
  const [resubmit, setResubmit] = useState({ open: false, request: null, ref: "", file: null });
  const [renewOpen, setRenewOpen] = useState(null); // {course_id, course_name} or null
  const [busy, setBusy] = useState(false);
  const [iapBusyId, setIapBusyId] = useState(null); // course_id currently starting IAP
  const [accessByCourse, setAccessByCourse] = useState({});
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = async () => {
    const [m, ev, av, ps, sys, pc, prefs, perCourse] = await Promise.all([
      api.get("/student/my-enrollments").then((r) => r.data),
      api.get("/student/enrollment-events").then((r) => r.data).catch(() => ({ events: [] })),
      api.get("/student/available-courses").then((r) => r.data),
      api.get("/payment-settings/public").then((r) => r.data).catch(() => null),
      api.get("/payment-systems/public").then((r) => r.data).catch(() => []),
      api.get("/student/profile/completion-status").then((r) => r.data).catch(() => null),
      api.get("/me/study-prefs").then((r) => r.data).catch(() => null),
      api.get("/student/access-status/per-course").then((r) => r.data).catch(() => []),
    ]);
    setAccessByCourse(Object.fromEntries((Array.isArray(perCourse) ? perCourse : []).map((s) => [s.course_id, s])));
    setData(m);
    setEvents((ev && ev.events) || []);
    setAvailable(av || []);
    setPaySettings(ps);
    setPaymentSystems(Array.isArray(sys) ? sys : []);
    setProfileCompletion(pc);
    setExamDatesByCourse((prefs && prefs.exam_dates_by_course) || {});
  };

  useEffect(() => { reload().catch(() => toast.error("Could not load enrollments")); }, []);

  // Deep-link support: /student/enrollments?renew=<course_id> auto-opens the
  // RenewSubscriptionModal preselected to that course. Fired by the notif
  // bell's "Renew now" button so students can pay in 1 tap from the drawer.
  useEffect(() => {
    if (!data) return;
    const renewId = searchParams.get("renew");
    if (!renewId) return;
    const match = (data.enrolled || []).find((c) => c.id === renewId);
    const paramName = searchParams.get("course_name");
    // Prefer the live enrollment name; fall back to the name carried in the
    // deep-link (from the notification payload) so the modal title reads
    // sensibly even when the course is no longer in the active enrolled list.
    setRenewOpen({
      id: renewId,
      course_name: match?.course_name || paramName || "Subscription",
    });
    // Clear the query params so refreshing the page won't reopen the modal.
    const next = new URLSearchParams(searchParams);
    next.delete("renew");
    next.delete("course_name");
    setSearchParams(next, { replace: true });
  }, [data, searchParams, setSearchParams]);

  const validateFile = (file) => {
    if (!file) return null;
    if (!ALLOWED_MIMES.includes(file.type)) { toast.error("Only JPG, PNG, or PDF files are allowed"); return null; }
    if (file.size > MAX_PROOF_BYTES) { toast.error("File must be 5MB or smaller"); return null; }
    return file;
  };

  const openPayMongo = () => {
    if (profileCompletion && !profileCompletion.is_complete) {
      toast.info("Please complete your profile before proceeding with payment.");
      nav("/student/profile/complete?next=/student/enrollments");
      return;
    }
    const link = paySettings?.paymongo_payment_link;
    if (!link) { toast.error("Payment link is not configured. Contact the administrator."); return; }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  /**
   * Trigger the native In-App Purchase sheet directly from the
   * enrollments list. On iOS this opens Apple's StoreKit sheet; on
   * Android it opens Google Play Billing. The student goes straight
   * from "Subscribe" to the platform purchase prompt without any
   * home-screen redirect. Access is granted server-side once the
   * receipt is validated for the matching course + tier.
   */
  const startNativePurchase = async (course) => {
    const productId = course?.pricing?.product_id;
    if (!course?.id) return;
    if (!productId) {
      toast.error("This course has no price tier configured yet. Please contact your administrator.");
      return;
    }
    setIapBusyId(course.id);
    try {
      await IAP.init();
      await IAP.buy({ courseId: course.id, productId });
      toast.success(`Purchase complete — ${course.course_name || "course"} is now fully unlocked!`);
      reload().catch(() => {});
    } catch (e) {
      toast.error(e?.message || "Could not start the purchase.");
    } finally {
      setIapBusyId(null);
    }
  };

  const submitRequest = async () => {
    if (!dialog.course) return;
    const required = paySettings?.payment_required_for_enrollment;
    if (required && profileCompletion && !profileCompletion.is_complete) {
      toast.info("Please complete your profile before submitting payment.");
      nav("/student/profile/complete?next=/student/enrollments");
      return;
    }
    if (required && !paySettings?.paymongo_payment_link) {
      toast.error("Payment link is not yet configured. Please contact the administrator.");
      return;
    }
    if (required && !dialog.ref.trim() && !dialog.file) {
      toast.error("Provide a payment reference number or upload proof of payment");
      return;
    }
    setBusy(true);
    try {
      if (required) {
        const fd = new FormData();
        fd.append("course_id", dialog.course.id);
        fd.append("note", dialog.note.trim().slice(0, 300));
        fd.append("payment_reference_number", dialog.ref.trim().slice(0, 80));
        if (dialog.file) fd.append("proof_of_payment", dialog.file);
        await api.post("/student/enrollment-requests/with-payment", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/student/enrollment-requests", {
          course_id: dialog.course.id,
          note: dialog.note.trim().slice(0, 300),
        });
      }
      toast.success(required ? "Request submitted — awaiting payment verification" : "Request submitted — awaiting admin approval");
      setDialog({ open: false, course: null, note: "", ref: "", file: null });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  const submitResubmit = async () => {
    if (!resubmit.request) return;
    if (!resubmit.ref.trim() && !resubmit.file) {
      toast.error("Provide a reference number or upload a new proof of payment");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payment_reference_number", resubmit.ref.trim().slice(0, 80));
      if (resubmit.file) fd.append("proof_of_payment", resubmit.file);
      await api.post(`/student/enrollment-requests/${resubmit.request.id}/resubmit-payment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Payment proof resubmitted — awaiting verification");
      setResubmit({ open: false, request: null, ref: "", file: null });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resubmit proof");
    } finally {
      setBusy(false);
    }
  };

  const enrollTemporary = async (course) => {
    setBusy(true);
    try {
      const r = await api.post("/student/enroll-temporary", { course_id: course.id });
      const days = r.data?.access_state?.days_remaining;
      const qLimit = r.data?.access_state?.question_limit;
      const parts = [];
      if (typeof days === "number") parts.push(`${days} day${days === 1 ? "" : "s"}`);
      if (qLimit) parts.push(`${qLimit} questions`);
      const detail = parts.length ? ` (${parts.join(" · ")})` : "";
      if (course.is_free || r.data?.access_state?.is_full) toast.success(`You now have full access to ${course.course_name}.`, { duration: 6000 });
      else toast.success(`You now have temporary access to ${course.course_name}${detail}. Pay any time to unlock full access.`, { duration: 6000 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start free trial");
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async (id) => {
    if (!window.confirm("Cancel this pending request?")) return;
    try {
      await api.delete(`/student/enrollment-requests/${id}`);
      toast.success("Request cancelled");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  // The tab to open on mount. Two sources drive this:
  //   1) `?tab=available|history|current` URL param — e.g. the home page's
  //      "Enroll Now" CTA appends `?tab=available` when the student has no
  //      current enrollment.
  //   2) Auto-fallback — if the student has zero current items, open
  //      "Available" so they can browse instead of staring at "You're not
  //      enrolled in any active course."
  const [tab, setTab] = useState("current");
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (requested === "available" || requested === "history" || requested === "current") {
      setTab(requested);
      return;
    }
    if (!data) return;
    const currentCount = (data.enrolled?.length || 0) + (data.requests || [])
      .filter((r) => r.status === "pending" && !r.revoked && !r.suspended).length;
    if (currentCount === 0) setTab("available");
  }, [data, searchParams]);

  if (!data) return <div className="text-slate-500" data-testid="enrollments-loading">Loading…</div>;

  const required = paySettings?.payment_required_for_enrollment;
  const linkConfigured = !!paySettings?.paymongo_payment_link;
  // PayMongo button only shows when the super admin has both enabled it AND
  // supplied a link — matches the picker gate used elsewhere.
  const paymongoActive = !!(paySettings?.paymongo_enabled && paySettings?.paymongo_payment_link);
  // Whether ANY manual (GCash, Maya, GoTyme, bank transfer) system is
  // enabled — matches the exact logic the PaymentMethodsPicker uses to
  // decide whether to render tiles. Loaded from /payment-systems/public.
  const hasManualSystems = paymentSystems.length > 0;
  // A payment channel of ANY kind is configured?  → suppress the amber
  // "Payment link is not yet configured" warning. The old check only
  // considered the PayMongo link and fired even after admins set up
  // GCash / Maya / bank-transfer instructions.
  const anyPaymentConfigured = paymongoActive || hasManualSystems;

  // Split requests into "active" (still awaiting action) and "history"
  // (resolved: rejected / cancelled / revoked / suspended request rows).
  const pendingRequests = (data.requests || []).filter(
    (r) => r.status === "pending" && !r.revoked && !r.suspended,
  );
  const historyRequests = (data.requests || []).filter(
    (r) => r.status !== "pending" || r.revoked || r.suspended,
  );
  const historyCount = historyRequests.length + (events?.length || 0);
  // iOS shows the in-app free-trial catalog (App Store 3.1.3 permits this
  // because no purchase happens — full payment still routes to the web).
  // Android currently keeps the "Manage on web" hand-off card.
  const availableCount = available?.length || 0;

  return (
    <div className="space-y-8" data-testid="student-enrollments-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> My Enrollments
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Courses &amp; requests</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
          View the courses you're enrolled in and start a free trial on any new course. You can pay
          to unlock full access any time — no payment is required to begin reviewing.
        </p>
      </div>

      {/* Primary pay/upgrade CTA — surfaces "Pay Now / View Payment Methods /
          Upload Receipt" for the student's current course so /student/enrollments
          becomes a true payment hub, not just a courses list. */}
      {!isNativeApp() && <AccessBanner />}

      <PageIntroCard
        pageKey="enrollments-tabs"
        title="Everything about your access, in one place"
        body="Current shows your active courses and pending requests. Available lists what you can enroll in next. History keeps every past receipt, rejection, and access change so you can audit anytime."
      />

      {/* Only show the "no payment channel configured yet" warning when:
          - payment IS required to enroll, AND
          - no PayMongo link is set AND no manual (GCash/Maya/bank) channel is enabled, AND
          - we are NOT running inside the native iOS app (where IAP is the only allowed path anyway).
        This prevents the amber warning from firing after admins wire up
        manual payment methods but haven't set a PayMongo link. */}
      {required && !anyPaymentConfigured && !isNativeApp() && (
        <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="payment-not-configured-warning">
          <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Payment link is not yet configured.</div>
            <div>You can still start a free trial on any course. Full-access payment will become available once the admin sets up the payment link.</div>
          </div>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="enrollments-tabs">
        <TabsList className="h-auto p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-full sm:w-auto grid grid-cols-3 sm:inline-flex sm:gap-1" data-testid="enrollments-tabs-list">
          <TabsTrigger value="current" data-testid="tab-current" className="h-9 px-3 sm:px-5 text-sm font-semibold">
            Current
            <span className="ml-1.5 text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
              {(data.enrolled?.length || 0) + pendingRequests.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="available" data-testid="tab-available" className="h-9 px-3 sm:px-5 text-sm font-semibold">
            Available
            {availableCount > 0 && (
              <span className="ml-1.5 text-[10px] tabular-nums text-slate-500 dark:text-slate-400">{availableCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="h-9 px-3 sm:px-5 text-sm font-semibold">
            History
            {historyCount > 0 && (
              <span className="ml-1.5 text-[10px] tabular-nums text-slate-500 dark:text-slate-400">{historyCount}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* -------- Current tab -------- */}
        <TabsContent value="current" className="mt-6 space-y-8" data-testid="tab-content-current">
      <section data-testid="enrolled-section">
        <h2 className="font-display font-bold text-lg mb-3">Currently Enrolled</h2>
        {data.enrolled.length === 0 ? (
          <Card className="p-6 text-sm text-slate-500 dark:text-slate-300" data-testid="enrolled-empty">
            You're not enrolled in any active course.
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {data.enrolled.map((c) => (
              <Card
                key={c.id}
                className={`p-5 border-slate-200 ${c.suspended ? "border-amber-300 bg-amber-50" : c.expiring_soon ? "border-orange-300 bg-orange-50/60" : ""}`}
                data-testid={`enrolled-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`h-10 w-10 rounded-md grid place-items-center ${c.suspended ? "bg-amber-100 text-amber-700" : c.expiring_soon ? "bg-orange-100 text-orange-700" : "bg-indigo-100 text-indigo-700"}`}>
                    <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {c.suspended && (
                      <Badge className="bg-amber-500 text-white" data-testid={`suspended-badge-${c.id}`}>
                        {c.auto_expired ? "Subscription Expired" : "Access Suspended"}
                      </Badge>
                    )}
                    {!c.suspended && c.expiring_soon && (
                      <Badge className="bg-orange-500 text-white" data-testid={`expiring-soon-badge-${c.id}`}>
                        Expires in {c.days_remaining ?? 0}d
                      </Badge>
                    )}
                    {c.is_primary && (<Badge className="bg-blue-100 text-blue-800 border-blue-200">Primary</Badge>)}
                  </div>
                </div>
                <h3 className="font-display font-bold text-lg mt-3 leading-tight">{c.course_name}</h3>
                {c.description && <p className="text-sm text-slate-500 dark:text-slate-300 mt-1 line-clamp-2">{c.description}</p>}
                {!c.suspended && c.access_expires_at && !accessByCourse[c.id]?.is_full && (
                  <div
                    className={`mt-2 text-[11px] font-medium ${c.expiring_soon ? "text-orange-800" : "text-slate-500 dark:text-slate-400"}`}
                    data-testid={`access-expiry-${c.id}`}
                  >
                    Access {c.days_remaining <= 0 ? "expired" : "expires"} on {(c.access_expires_at || "").slice(0, 10)}
                    {c.days_remaining !== null && c.days_remaining !== undefined && (
                      <> · <span className="font-semibold">{c.days_remaining} day{c.days_remaining === 1 ? "" : "s"} remaining</span></>
                    )}
                  </div>
                )}
                {c.suspended ? (
                  <div className="mt-3 rounded-md bg-white border border-amber-200 p-3" data-testid={`suspended-notice-${c.id}`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <div className="font-semibold text-amber-900">
                          {c.auto_expired
                            ? "Your subscription to this course has expired."
                            : "Your access to this course has been suspended."}
                        </div>
                        {c.suspended_reason && (
                          <div className="mt-1 text-slate-700"><span className="font-medium">Reason:</span> {c.suspended_reason}</div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {c.auto_expired ? (
                            <Button
                              size="sm"
                              className="h-8 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold"
                              onClick={() => setRenewOpen({ id: c.id, course_name: c.course_name })}
                              data-testid={`renew-btn-${c.id}`}
                            >
                              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Renew Subscription
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-amber-300 text-amber-900"
                            onClick={() => nav("/student/support")}
                            data-testid={`enrolled-suspended-contact-support-${c.id}`}
                          >
                            Contact Support
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <CourseExamDateInput courseId={c.id} allDates={examDatesByCourse} />
                    {accessByCourse[c.id]?.is_full && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <SubscriptionStatus state={accessByCourse[c.id]} />
                      </div>
                    )}
                    {/* Per-card Pay/Subscribe CTA — appears when the course has
                        no active paid access. Uses the course's assigned price
                        tier so students with multiple courses can see exactly
                        which button unlocks which course. */}
                    {!c.access_expires_at && !accessByCourse[c.id]?.is_full && (c.pricing?.price_display || (isNativeApp() && c.pricing?.product_id)) && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[11px] text-slate-500">
                          Full access{c.pricing.price_display && <>: <span className="font-mono font-semibold text-slate-700">{c.pricing.price_display}</span></>} · {c.pricing.months}mo
                        </div>
                        <Button
                          size="sm"
                          className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                          disabled={iapBusyId === c.id}
                          onClick={() => {
                            if (isNativeApp()) {
                              // Skip the home-screen banner and jump straight
                              // to the native purchase sheet for THIS course.
                              startNativePurchase(c);
                            } else {
                              nav(`/student/enrollments?tab=current#pay-${c.id}`);
                              scrollTo({ top: 0, behavior: "smooth" });
                            }
                          }}
                          data-testid={`enrolled-pay-${c.id}`}
                        >
                          {isNativeApp() ? (
                            iapBusyId === c.id
                              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : (isNativeIOS() ? <Apple className="w-3.5 h-3.5 mr-1" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />)
                          ) : (
                            <CreditCard className="w-3.5 h-3.5 mr-1" />
                          )}
                          {iapBusyId === c.id ? "Starting…" : (c.pricing.price_display ? `Subscribe · ${c.pricing.price_display}` : "Subscribe")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      {pendingRequests.length > 0 && (
        <section data-testid="pending-requests-section">
          <h2 className="font-display font-bold text-lg mb-3">Pending Requests</h2>
          <Card className="p-0 border-slate-200 overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {pendingRequests.map((r) => {
                const meta = STATUS_STYLES.pending;
                const Icon = meta.icon;
                const payMeta = PAYMENT_STATUS_STYLES[r.payment_status] || null;
                const canResubmit = r.payment_status === "rejected";
                const needsInitialUpload =
                  r.payment_required &&
                  r.payment_status === "pending" &&
                  !r.payment_reference_number;
                return (
                  <li key={r.id} className="p-4 flex items-start gap-3" data-testid={`request-row-${r.id}`}>
                    <div className={`h-9 w-9 rounded-lg grid place-items-center ${meta.color} border shrink-0`}>
                      <Icon className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{r.course_name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Requested {(r.requested_at || "").slice(0, 10)}
                      </div>
                      {r.note && <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 italic">"{r.note}"</div>}
                      {r.payment_reference_number && (
                        <div className="text-[11px] text-slate-500 mt-0.5">Ref: <span className="font-mono">{r.payment_reference_number}</span></div>
                      )}
                      {r.payment_rejection_reason && (
                        <div className="text-xs text-rose-700 mt-1">Payment rejected: {r.payment_rejection_reason}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0 space-y-1 flex flex-col items-end">
                      <Badge variant="outline" className={meta.color + " border"} data-testid={`request-status-badge-${r.id}`}>{meta.label}</Badge>
                      {payMeta && (<Badge variant="outline" className={payMeta.color + " border"} data-testid={`payment-status-${r.id}`}>{payMeta.label}</Badge>)}
                      {canResubmit && (
                        <Button variant="outline" size="sm" className="mt-1 h-7 px-2 border-amber-300 text-amber-800"
                          onClick={() => setResubmit({ open: true, request: r, ref: "", file: null })}
                          data-testid={`resubmit-payment-${r.id}`}>
                          <RefreshCcw className="w-3 h-3 mr-1" /> Resubmit Proof
                        </Button>
                      )}
                      {needsInitialUpload && (
                        <Button size="sm" className="mt-1 h-7 px-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => setResubmit({ open: true, request: r, ref: "", file: null })}
                          data-testid={`upload-payment-${r.id}`}>
                          <Upload className="w-3 h-3 mr-1" /> Upload Payment Proof
                        </Button>
                      )}
                      {!canResubmit && !needsInitialUpload && (
                        <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 h-7 px-2"
                          onClick={() => cancelRequest(r.id)} data-testid={`request-cancel-${r.id}`}>Cancel</Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>
      )}
        </TabsContent>

        {/* -------- History tab (access updates + resolved requests) -------- */}
        <TabsContent value="history" className="mt-6 space-y-8" data-testid="tab-content-history">

      {events.length > 0 && (
        <section data-testid="access-updates-section">
          <h2 className="font-display font-bold text-lg mb-3">Recent Access Updates</h2>
          <div className="space-y-3">
            {events.map((e) => {
              const tone = e.action === "suspended"
                ? { border: "border-amber-300", bg: "bg-amber-50", chip: "bg-amber-500 text-white", label: "Access Suspended" }
                : e.action === "restored"
                ? { border: "border-emerald-300", bg: "bg-emerald-50", chip: "bg-emerald-500 text-white", label: "Access Restored" }
                : { border: "border-red-300", bg: "bg-red-50", chip: "bg-red-500 text-white", label: "Enrollment Revoked" };
              const helper = e.action === "suspended"
                ? "Your access has been paused by an administrator."
                : e.action === "restored"
                ? "Your access has been restored by an administrator."
                : "Your enrollment was revoked by an administrator.";
              return (
                <Card
                  key={e.id}
                  className={`p-4 border ${tone.border} ${tone.bg}`}
                  data-testid={`access-event-${e.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded ${tone.chip} shrink-0`}>
                      {tone.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">
                        {e.course_name || "Course"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {new Date(e.at).toLocaleString()}
                        {e.admin_name ? ` — by ${e.admin_name}` : ""}
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">{helper}</p>
                      {e.reason && (
                        <div className="mt-2 text-xs bg-white/70 border border-slate-200 rounded p-2">
                          <span className="font-semibold">Reason:</span> {e.reason}
                        </div>
                      )}
                      {e.action !== "restored" && (
                        <div className="mt-2 text-xs text-slate-600">
                          Please{" "}
                          <button
                            type="button"
                            onClick={() => nav("/student/support")}
                            className="underline text-blue-700 hover:text-blue-800 font-semibold"
                            data-testid={`access-event-contact-support-${e.id}`}
                          >contact support</button>
                          {e.action === "deleted" ? " to re-enroll." : " to restore access."}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      <section data-testid="requests-section">
        <h2 className="font-display font-bold text-lg mb-3">My Requests</h2>
        {historyRequests.length === 0 ? (
          <Card className="p-6 text-sm text-slate-500 dark:text-slate-300" data-testid="requests-empty">No past requests yet.</Card>
        ) : (
          <Card className="p-0 border-slate-200 overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {historyRequests.map((r) => {
                const effectiveStatus = r.revoked ? "revoked" : r.suspended ? "suspended" : r.status;
                const meta = STATUS_STYLES[effectiveStatus] || STATUS_STYLES.pending;
                const Icon = meta.icon;
                const payMeta = r.revoked || r.suspended ? null : (PAYMENT_STATUS_STYLES[r.payment_status] || null);
                const canResubmit = r.status === "pending" && r.payment_status === "rejected";
                // A student who created a pending enrollment WITHOUT payment
                // proof (or hasn't paid yet) needs the same upload dialog —
                // the backend `resubmit-payment` endpoint accepts both the
                // "pending" and "rejected" cases. Only show this for
                // payment-required courses without a reference already on file.
                const needsInitialUpload =
                  r.status === "pending" &&
                  r.payment_required &&
                  r.payment_status === "pending" &&
                  !r.payment_reference_number;
                const rowTone = r.revoked
                  ? "bg-rose-50/60"
                  : r.suspended
                  ? "bg-amber-50/60"
                  : "";
                return (
                  <li key={r.id} className={`p-4 flex items-start gap-3 ${rowTone}`} data-testid={`request-row-${r.id}`}>
                    <div className={`h-9 w-9 rounded-lg grid place-items-center ${meta.color} border shrink-0`}>
                      <Icon className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm truncate ${r.revoked ? "line-through text-slate-500" : ""}`}>{r.course_name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Requested {(r.requested_at || "").slice(0, 10)}
                        {r.reviewed_at && <> · Reviewed {(r.reviewed_at || "").slice(0, 10)}</>}
                        {r.revoked && r.revoked_at && <> · Revoked {(r.revoked_at || "").slice(0, 10)}</>}
                      </div>
                      {r.note && <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 italic">"{r.note}"</div>}
                      {r.payment_reference_number && !r.revoked && (
                        <div className="text-[11px] text-slate-500 mt-0.5">Ref: <span className="font-mono">{r.payment_reference_number}</span></div>
                      )}
                      {r.payment_rejection_reason && (
                        <div className="text-xs text-rose-700 mt-1">Payment rejected: {r.payment_rejection_reason}</div>
                      )}
                      {r.reject_reason && (<div className="text-xs text-rose-700 mt-1">Admin note: {r.reject_reason}</div>)}
                      {r.revoked && (
                        <div className="mt-2 text-xs bg-white/80 border border-rose-200 rounded p-2" data-testid={`request-revoked-info-${r.id}`}>
                          <div className="font-semibold text-rose-800">Your enrollment was revoked by an administrator.</div>
                          {r.revoked_reason && <div className="mt-0.5 text-slate-700"><span className="font-medium">Reason:</span> {r.revoked_reason}</div>}
                          {r.revoked_by_name && <div className="mt-0.5 text-slate-500">By {r.revoked_by_name}</div>}
                          <div className="mt-1 text-slate-600">
                            Please{" "}
                            <button
                              type="button"
                              onClick={() => nav("/student/support")}
                              className="underline text-blue-700 hover:text-blue-800 font-semibold"
                              data-testid={`request-revoked-contact-support-${r.id}`}
                            >contact support</button>{" "}
                            to re-enroll.
                          </div>
                        </div>
                      )}
                      {r.suspended && !r.revoked && (
                        <div className="mt-2 text-xs bg-white/80 border border-amber-200 rounded p-2" data-testid={`request-suspended-info-${r.id}`}>
                          <div className="font-semibold text-amber-900">Your access is currently suspended.</div>
                          {r.suspended_reason && <div className="mt-0.5 text-slate-700"><span className="font-medium">Reason:</span> {r.suspended_reason}</div>}
                          <div className="mt-1 text-slate-600">
                            Please{" "}
                            <button
                              type="button"
                              onClick={() => nav("/student/support")}
                              className="underline text-blue-700 hover:text-blue-800 font-semibold"
                              data-testid={`request-suspended-contact-support-${r.id}`}
                            >contact support</button>{" "}
                            to restore access.
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0 space-y-1 flex flex-col items-end">
                      <Badge variant="outline" className={meta.color + " border"} data-testid={`request-status-badge-${r.id}`}>{meta.label}</Badge>
                      {payMeta && (<Badge variant="outline" className={payMeta.color + " border"} data-testid={`payment-status-${r.id}`}>{payMeta.label}</Badge>)}
                      {canResubmit && (
                        <Button variant="outline" size="sm" className="mt-1 h-7 px-2 border-amber-300 text-amber-800"
                          onClick={() => setResubmit({ open: true, request: r, ref: "", file: null })}
                          data-testid={`resubmit-payment-${r.id}`}>
                          <RefreshCcw className="w-3 h-3 mr-1" /> Resubmit Proof
                        </Button>
                      )}
                      {needsInitialUpload && (
                        <Button size="sm" className="mt-1 h-7 px-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => setResubmit({ open: true, request: r, ref: "", file: null })}
                          data-testid={`upload-payment-${r.id}`}>
                          <Upload className="w-3 h-3 mr-1" /> Upload Payment Proof
                        </Button>
                      )}
                      {r.status === "pending" && !canResubmit && !needsInitialUpload && (
                        <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 h-7 px-2"
                          onClick={() => cancelRequest(r.id)} data-testid={`request-cancel-${r.id}`}>Cancel</Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </section>
        </TabsContent>

        {/* -------- Available tab -------- */}
        <TabsContent value="available" className="mt-6" data-testid="tab-content-available">
      <section data-testid="available-section">
        <h2 className="font-display font-bold text-lg mb-3">Browse More Courses</h2>
        {available.length === 0 ? (
          <Card className="p-6 text-sm text-slate-500 dark:text-slate-300" data-testid="available-empty">No other courses available right now.</Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {available.map((c) => {
              const dayLimit = c.limited_access_days ?? 3;
              const qLimit = c.limited_access_question_limit ?? 0;
              return (
                <Card key={c.id} className="p-5 border-slate-200 dark:border-slate-700 flex flex-col" data-testid={`available-card-${c.id}`}>
                  <div className="h-10 w-10 rounded-md bg-violet-100 text-violet-700 grid place-items-center">
                    <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-bold text-base mt-3 leading-tight">{c.course_name}</h3>
                  {c.description && <p className="text-sm text-slate-500 dark:text-slate-300 mt-1 line-clamp-3 flex-1">{c.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                    {c.is_free ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`available-card-${c.id}-free`}>Free course · Full access</Badge>
                    ) : (
                      <>
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Free trial</Badge>
                        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">{dayLimit}-day access</Badge>
                      </>
                    )}
                    {!c.is_free && qLimit > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">{qLimit} questions</Badge>}
                    {c.pricing?.price_display && (
                      <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-200 font-mono" data-testid={`available-card-${c.id}-price`}>
                        Full: {c.pricing.price_display} · 6mo
                      </Badge>
                    )}
                  </div>
                  <Button
                    className="mt-4 bg-violet-700 hover:bg-violet-800"
                    onClick={() => enrollTemporary(c)}
                    disabled={busy}
                    data-testid={`enroll-temporary-btn-${c.id}`}
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> {c.is_free ? "Enroll Now (Free)" : "Enroll Now (Free Trial)"}
                  </Button>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 text-center mt-2">
                    {c.is_free ? "Full access to every module — no payment needed." : "Pay any time later to unlock full access."}
                  </p>
                </Card>
              );
            })}
          </div>
        )}
      </section>
        </TabsContent>
      </Tabs>

      {/* Enrollment + Payment Dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" /> Complete Your Enrollment Payment
            </DialogTitle>
            <DialogDescription>
              Selected course: <span className="font-semibold">{dialog.course?.course_name}</span>
            </DialogDescription>
          </DialogHeader>

          {required && paySettings?.payment_instructions && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900 dark:bg-blue-50 dark:text-blue-900 whitespace-pre-line" data-testid="payment-instructions">
              {paySettings.payment_instructions}
            </div>
          )}

          {required && paymongoActive && (
            <Button onClick={openPayMongo} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="pay-via-paymongo-btn" disabled={!linkConfigured}>
              <Wallet className="w-4 h-4 mr-2" /> Pay via PayMongo <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </Button>
          )}

          {required && (
            <>
              <div>
                <Label htmlFor="ref">Payment Reference Number</Label>
                <Input id="ref" value={dialog.ref}
                  onChange={(e) => setDialog({ ...dialog, ref: e.target.value })}
                  placeholder="e.g., 4231 0987 2233"
                  className="font-mono mt-1.5"
                  data-testid="payment-ref-input" />
              </div>
              <div>
                <Label htmlFor="proof">Upload Proof of Payment</Label>
                <Input id="proof" type="file" accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => setDialog({ ...dialog, file: validateFile(e.target.files?.[0] || null) })}
                  className="mt-1.5 cursor-pointer file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700"
                  data-testid="proof-file-input" />
                <p className="text-[11px] text-slate-500 mt-1">JPG, PNG, or PDF (max 5MB). Either reference number or proof file is required.</p>
                {dialog.file && (
                  <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><Upload className="w-3 h-3" /> {dialog.file.name}</div>
                )}
              </div>
            </>
          )}

          <div>
            <Label>Note to admin <span className="text-slate-400 text-xs ml-1">optional ({dialog.note.length}/300)</span></Label>
            <Textarea value={dialog.note} onChange={(e) => setDialog({ ...dialog, note: e.target.value.slice(0, 300) })}
              placeholder="Anything else the admin should know?" rows={2} data-testid="request-note-input" />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialog({ open: false, course: null, note: "", ref: "", file: null })}>Cancel</Button>
            <Button onClick={submitRequest} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="request-submit-btn">
              {busy ? "Submitting…" : (required ? "Submit Enrollment Request" : "Submit Request")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Proof Dialog — used for both initial upload AND re-upload after rejection */}
      <Dialog open={resubmit.open} onOpenChange={(o) => setResubmit((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="resubmit-dialog">
          <DialogHeader>
            <DialogTitle>
              {resubmit.request?.payment_status === "rejected" ? "Resubmit Proof of Payment" : "Upload Payment Proof"}
            </DialogTitle>
            <DialogDescription>
              {resubmit.request?.payment_status === "rejected" ? (
                <>Your previous proof for <span className="font-semibold">{resubmit.request?.course_name}</span> was rejected. Upload a new screenshot or provide an updated reference number.</>
              ) : (
                <>Submit your payment proof for <span className="font-semibold">{resubmit.request?.course_name}</span>. See <a href="/payment-instructions" className="underline text-indigo-700" target="_blank" rel="noreferrer">Payment Instructions</a> for how to pay via GCash, Maya, or PayMongo.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="re_ref">Payment Reference Number</Label>
            <Input id="re_ref" value={resubmit.ref}
              onChange={(e) => setResubmit({ ...resubmit, ref: e.target.value })}
              className="font-mono mt-1.5"
              placeholder="e.g., 4231 0987 2233"
              data-testid="resubmit-ref-input" />
          </div>
          <div>
            <Label htmlFor="re_proof">Upload Proof</Label>
            <Input id="re_proof" type="file" accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setResubmit({ ...resubmit, file: validateFile(e.target.files?.[0] || null) })}
              className="mt-1.5 cursor-pointer file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700"
              data-testid="resubmit-file-input" />
            <p className="text-[11px] text-slate-500 mt-1">JPG, PNG, or PDF (max 5 MB). Either reference number or proof file is required.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setResubmit({ open: false, request: null, ref: "", file: null })}>Cancel</Button>
            <Button onClick={submitResubmit} disabled={busy}
              className={resubmit.request?.payment_status === "rejected" ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"}
              data-testid="resubmit-submit-btn">
              {busy ? "Submitting…" : (resubmit.request?.payment_status === "rejected" ? "Resubmit Proof" : "Submit Proof")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RenewSubscriptionModal
        open={!!renewOpen}
        course={renewOpen ? { id: renewOpen.id, course_name: renewOpen.course_name } : null}
        onClose={() => setRenewOpen(null)}
        onSubmitted={() => { setRenewOpen(null); reload().catch(() => {}); }}
      />
    </div>
  );
}
